import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SearchBar from "@/components/shop/SearchBar";
import ProductList from "@/components/shop/ProductList";
import { mockProducts } from "@/utils/mockProducts";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Store } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import CartSheet from "@/components/shop/CartSheet";
import { ShopHero } from "@/components/shop/ShopHero";
import { ProductCategories } from "@/components/home/ProductCategories";
import { useToast } from "@/components/ui/use-toast";
import type { Product } from "@/types/shop";

const Shop = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const navigate = useNavigate();
  const { items } = useCart();
  const { toast } = useToast();

  const { data: products, isLoading } = useQuery({
    queryKey: ['shop-products'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('vendor_products')
          .select(`
            *,
            vendors (
              name,
              is_verified,
              rating
            )
          `)
          .eq('status', 'active');

        if (error) {
          console.error('Error fetching products:', error);
          toast({
            title: "Error",
            description: "Could not fetch products. Please try again later.",
            variant: "destructive",
          });
          return mockProducts;
        }

        return data.length > 0 ? data : mockProducts;
      } catch (error) {
        console.error('Unexpected error:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again later.",
          variant: "destructive",
        });
        return mockProducts;
      }
    },
  });

  const filteredProducts = products?.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCategoryClick = (category: string) => {
    // Filter products by category
    setSearchTerm(category);
    toast({
      title: "Category Selected",
      description: `Showing products in ${category} category`,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
        <div className="container flex h-16 items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="hover:bg-primary/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2.5">
              <Store className="h-7 w-7 text-primary" />
              <span className="font-semibold text-lg text-primary hidden md:block tracking-tight">
                Obotan Shop
              </span>
            </div>
          </div>
          
          <div className="flex-1 max-w-2xl mx-auto">
            <SearchBar 
              searchTerm={searchTerm}
              viewMode={viewMode}
              onSearchChange={setSearchTerm}
              onViewModeChange={setViewMode}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <Sheet>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="relative hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {items.length > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -right-2 -top-2 h-5 w-5 rounded-full p-0 flex items-center justify-center"
                    >
                      {items.length}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-lg">
                <CartSheet />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <ShopHero />
      
      <div className="bg-gray-50 py-8">
        <div className="container">
          <ProductCategories onCategoryClick={handleCategoryClick} />
        </div>
      </div>

      <main className="container py-8 lg:py-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          {/* Sidebar filters */}
          <aside className="col-span-12 md:col-span-3 space-y-6">
            <div className="bg-white p-5 rounded-lg shadow-sm border">
              <h3 className="font-semibold mb-4 text-lg">Department</h3>
              <ul className="space-y-3 text-sm">
                {["Groceries", "Frozen Food", "Commercial Vehicles", "Kitchen & Dining"].map((dept) => (
                  <li 
                    key={dept}
                    onClick={() => handleCategoryClick(dept)}
                    className="text-primary/90 cursor-pointer hover:text-primary transition-colors"
                  >
                    {dept}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm border">
              <h3 className="font-semibold mb-4 text-lg">Customer Rating</h3>
              <ul className="space-y-3">
                {[5, 4].map((rating) => (
                  <li 
                    key={rating}
                    onClick={() => {
                      toast({
                        title: "Filter Applied",
                        description: `Showing products with ${rating}+ stars`,
                      });
                    }}
                    className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors"
                  >
                    <span className="text-yellow-400">{"★".repeat(rating)}</span>
                    {rating < 5 && <span className="text-gray-400">{"★".repeat(5-rating)}</span>}
                    <span className="text-sm">& Up</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm border">
              <h3 className="font-semibold mb-4 text-lg">Price</h3>
              <ul className="space-y-3 text-sm">
                {["Under $25", "$25 to $50", "$50 to $100", "$100 & Above"].map((range) => (
                  <li 
                    key={range}
                    onClick={() => {
                      toast({
                        title: "Price Filter Applied",
                        description: `Showing products ${range}`,
                      });
                    }}
                    className="text-primary/90 cursor-pointer hover:text-primary transition-colors"
                  >
                    {range}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main content */}
          <div className="col-span-12 md:col-span-9">
            <ProductList products={filteredProducts} isLoading={isLoading} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Shop;